import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  FileSpreadsheet, Link2, Unlink, ArrowDownToLine, ArrowUpFromLine,
  Copy, RefreshCcw, ShieldCheck, AlertTriangle, ExternalLink, Info,
} from 'lucide-react';
import { fmtDateTime } from '@/lib/format';

const MANAGER_ROLES = ['owner', 'dm_creative', 'dm_technical', 'dm_architecture'];

/** Master Project Data ↔ Google Sheet link card.
 *  Same UX as TeamDirectorySheetPanel — uses the same service-account credential;
 *  binds a separate sheet just for master-projects rows. */
export function MasterProjectSheetPanel({ onDataChanged }) {
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.includes(user?.role);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/master-projects/sheet/status');
      setStatus(r.data);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const pull = async () => {
    setBusy('pull');
    try {
      const r = await api.post('/master-projects/sheet/pull');
      const s = r.data.stats;
      toast.success(`Synced from sheet — ${s.updated} updated, ${s.created} new.`);
      await refresh();
      onDataChanged && onDataChanged();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };
  const push = async () => {
    setBusy('push');
    try {
      const r = await api.post('/master-projects/sheet/push');
      toast.success(`Wrote ${r.data.stats.rows_written} projects to the sheet.`);
      await refresh();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };
  const unlink = async () => {
    if (!window.confirm('Unlink this Google Sheet? The sheet contents are not modified.')) return;
    setBusy('unlink');
    try {
      await api.delete('/master-projects/sheet/link');
      toast.success('Sheet unlinked.');
      await refresh();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="card p-4 elev-1 text-[12px] text-[hsl(var(--foreground-muted))]" data-testid="mpsheet-loading">
      Checking Master Project Data sheet link…
    </div>;
  }
  if (!status) return null;

  return (
    <div className="card p-4 elev-1" data-testid="mpsheet-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md grid place-items-center bg-[hsl(var(--success)/.12)] text-[hsl(var(--success))] flex-none">
          <FileSpreadsheet size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] font-semibold">Google Sheet sync — Master Project Data</div>
            {!status.configured && (
              <span className="chip status-cancelled" data-testid="mpsheet-not-configured">
                <AlertTriangle size={10} /> Service account not set up
              </span>
            )}
            {status.configured && !status.linked && (
              <span className="chip status-pending" data-testid="mpsheet-not-linked">Not linked</span>
            )}
            {status.linked && (
              <span className="chip status-done" data-testid="mpsheet-linked">
                <ShieldCheck size={10} /> Linked
              </span>
            )}
          </div>
          <p className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">
            App is the source of truth; app edits push back on demand. Standalone mode works when no sheet is linked.
          </p>

          {status.configured && status.service_account_email && (
            <div className="mt-3 flex items-center gap-2 flex-wrap p-2 rounded-md bg-[hsl(var(--surface-2))]"
                 data-testid="mpsheet-sa-email">
              <Info size={12} className="text-[hsl(var(--primary))]" />
              <span className="text-[11.5px] text-[hsl(var(--foreground-muted))]">Share the sheet as Editor with:</span>
              <span className="font-mono text-[12px] font-semibold">{status.service_account_email}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(status.service_account_email); toast.success('Copied'); }}
                className="text-[11px] px-1.5 py-0.5 rounded hover:bg-[hsl(var(--surface-1))] flex items-center gap-1"
                data-testid="mpsheet-copy-sa"
              >
                <Copy size={10} /> Copy
              </button>
            </div>
          )}

          {!status.configured && (
            <div className="mt-3 p-3 rounded-md bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning)/.35)]"
                 data-testid="mpsheet-setup-hint">
              <div className="flex items-start gap-2 text-[12px] text-[hsl(var(--foreground))]">
                <AlertTriangle size={13} className="text-[hsl(var(--warning))] flex-none mt-0.5" />
                <div>
                  <div className="font-semibold">Service account key isn&rsquo;t configured yet.</div>
                  <div className="text-[hsl(var(--foreground-muted))] mt-1">
                    Ask the app admin to set <span className="font-mono">GOOGLE_SERVICE_ACCOUNT_JSON_B64</span> in <span className="font-mono">backend/.env</span> and restart. The same credential drives both the Team Directory and Master Project Data sheets.
                  </div>
                  <button onClick={() => setHelpOpen(true)} className="mt-1.5 text-[12px] font-semibold text-[hsl(var(--primary))] underline underline-offset-2"
                          data-testid="mpsheet-open-help">
                    Show setup steps
                  </button>
                </div>
              </div>
            </div>
          )}

          {status.linked && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11.5px]" data-testid="mpsheet-link-info">
              <InfoRow label="Sheet"
                       value={<a href={status.link.sheet_url} target="_blank" rel="noreferrer"
                                 className="text-[hsl(var(--primary))] hover:underline flex items-center gap-1">
                                Open sheet <ExternalLink size={10} />
                              </a>} />
              <InfoRow label="Tab" value={<span className="font-mono">{status.link.tab_name}</span>} />
              <InfoRow label="Linked by" value={<span>{status.link.linked_by}</span>} />
              <InfoRow label="Last pull"
                       value={status.link.last_pull_at
                                ? <span>{fmtDateTime(status.link.last_pull_at)} · +{status.link.last_pull_stats?.created ?? 0} new, {status.link.last_pull_stats?.updated ?? 0} updated</span>
                                : <span className="italic text-[hsl(var(--foreground-subtle))]">Never</span>} />
              <InfoRow label="Last push"
                       value={status.link.last_push_at
                                ? <span>{fmtDateTime(status.link.last_push_at)} · {status.link.last_push_stats?.rows_written ?? 0} rows</span>
                                : <span className="italic text-[hsl(var(--foreground-subtle))]">Never</span>} />
              <InfoRow label="Expected headers"
                       value={<span className="font-mono text-[10.5px] break-all">{status.expected_headers.slice(0, 8).join(', ')} +{status.expected_headers.length - 8} more</span>} />
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex flex-col gap-1.5" data-testid="mpsheet-actions">
            {!status.linked ? (
              <Button size="sm" onClick={() => setLinkOpen(true)} disabled={!status.configured}
                      data-testid="mpsheet-btn-link">
                <Link2 size={13} className="mr-1.5" />Link Sheet
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={pull} disabled={!!busy} data-testid="mpsheet-btn-pull">
                  <ArrowDownToLine size={13} className="mr-1.5" />
                  {busy === 'pull' ? 'Pulling…' : 'Pull from Sheet'}
                </Button>
                <Button size="sm" variant="outline" onClick={push} disabled={!!busy} data-testid="mpsheet-btn-push">
                  <ArrowUpFromLine size={13} className="mr-1.5" />
                  {busy === 'push' ? 'Pushing…' : 'Push to Sheet'}
                </Button>
                <Button size="sm" variant="ghost" onClick={unlink} disabled={!!busy} data-testid="mpsheet-btn-unlink">
                  <Unlink size={13} className="mr-1.5" />Unlink
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={refresh} data-testid="mpsheet-btn-refresh">
              <RefreshCcw size={13} className="mr-1.5" />Refresh
            </Button>
            <button onClick={() => setHelpOpen(true)} className="text-[11px] text-[hsl(var(--primary))] underline underline-offset-2 mt-1"
                    data-testid="mpsheet-btn-help">
              How does this work?
            </button>
          </div>
        )}
      </div>

      <LinkDialog open={linkOpen} onOpenChange={setLinkOpen} onLinked={refresh}
                  serviceAccountEmail={status.service_account_email} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} serviceAccountEmail={status.service_account_email} />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function LinkDialog({ open, onOpenChange, onLinked, serviceAccountEmail }) {
  const [url, setUrl] = useState('');
  const [tab, setTab] = useState('Sheet1');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setUrl(''); setTab('Sheet1'); setBusy(false); } }, [open]);
  const submit = async () => {
    if (!url.trim()) { toast.error('Paste a Google Sheets URL.'); return; }
    setBusy(true);
    try {
      await api.post('/master-projects/sheet/link', { url: url.trim(), tab_name: tab.trim() || 'Sheet1' });
      toast.success('Sheet linked.');
      onOpenChange(false); onLinked();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="mpsheet-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 size={16} className="text-[hsl(var(--primary))]" />Link a Google Sheet</DialogTitle>
          <DialogDescription>Use a separate sheet from the Team Directory. Share it as Editor with the service account first.</DialogDescription>
        </DialogHeader>
        {serviceAccountEmail && (
          <div className="p-2 rounded bg-[hsl(var(--surface-2))] flex items-center gap-2 flex-wrap text-[11.5px]">
            <Info size={11} className="text-[hsl(var(--primary))]" />
            <span className="text-[hsl(var(--foreground-muted))]">Share with:</span>
            <span className="font-mono font-semibold">{serviceAccountEmail}</span>
          </div>
        )}
        <div className="space-y-3 pt-1">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Sheet URL</label>
            <Input value={url} onChange={e => setUrl(e.target.value)}
                   placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                   data-testid="mpsheet-link-url" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Tab name</label>
            <Input value={tab} onChange={e => setTab(e.target.value)} placeholder="Sheet1" data-testid="mpsheet-link-tab" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="mpsheet-link-submit">{busy ? 'Linking…' : 'Link Sheet'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpDialog({ open, onOpenChange, serviceAccountEmail }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[80vh] overflow-y-auto" data-testid="mpsheet-help-dialog">
        <DialogHeader>
          <DialogTitle>How the Master Project Data sync works</DialogTitle>
          <DialogDescription>Same service-account credential as Team Directory — bind a separate sheet just for project rows.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div>
            <div className="font-semibold mb-1">One-time setup (app admin)</div>
            <ol className="list-decimal ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li>Open <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline">Google Cloud Console</a> → create/select a project.</li>
              <li><strong>APIs &amp; Services → Library</strong> → search <span className="font-mono">Google Sheets API</span> → <em>Enable</em>.</li>
              <li><strong>IAM &amp; Admin → Service Accounts</strong> → create one (no project role needed).</li>
              <li>Open the service account → <strong>Keys → Add key → Create new key → JSON</strong>. Download.</li>
              <li>Base64-encode: <span className="font-mono">base64 -i key.json | tr -d &apos;\n&apos;</span></li>
              <li>Paste the single line into <span className="font-mono">backend/.env</span> as <span className="font-mono">GOOGLE_SERVICE_ACCOUNT_JSON_B64=…</span>.</li>
              <li>Restart the backend. The same credential now powers both sheets.</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold mb-1">Every sheet setup (Owner / Lead)</div>
            <ol className="list-decimal ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li>Create a NEW Google Sheet (separate from Team Directory) — e.g. &ldquo;Studio · Master Project Data&rdquo;.</li>
              <li>Click <strong>Share</strong> → add <span className="font-mono">{serviceAccountEmail || '(not configured yet)'}</span> as <strong>Editor</strong>. Uncheck &ldquo;Notify people&rdquo;.</li>
              <li>Copy the browser URL.</li>
              <li>Back here → <strong>Link Sheet</strong> → paste the URL and confirm the tab name.</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold mb-1">Sheet contract — one value per cell</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
              Row 1 headers (auto-written if the tab is empty):
            </div>
            <div className="mt-1 font-mono text-[11.5px] bg-[hsl(var(--surface-2))] p-2 rounded whitespace-pre-wrap break-all">
              id · project_name · client · project_stage · status · work_order_number · work_order_date · tenure_start_date · tenure_end_date · total_amount · paid_amount · unpaid_amount · creative_lead_email · technical_lead_email · architecture_lead_email · team_member_emails · milestones · notes
            </div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1.5">
              <strong>project_stage</strong> must be one of: <span className="font-mono">strategy_concept, schematic, dd, tdp, construction_admin_closure</span>.
              <br />
              <strong>status</strong> must be one of: <span className="font-mono">active, on_hold, closed</span>.
              <br />
              <strong>team_member_emails</strong> — comma-separated user emails.
              <br />
              <strong>milestones</strong> — comma-separated, each milestone as <span className="font-mono">Name|status|YYYY-MM-DD|amount</span> (e.g. <span className="font-mono">Concept freeze|done|2026-08-15|150000</span>). Status must be one of <span className="font-mono">pending / in_progress / done / invoiced / paid</span>.
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">What the buttons do</div>
            <ul className="list-disc ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li><strong>Pull from Sheet</strong> — updates matching projects by <span className="font-mono">id</span> (or name+client). Creates a new project if no match. Milestones with matching names are updated in place; unknown emails are skipped (never creates users).</li>
              <li><strong>Push to Sheet</strong> — overwrites rows 2+ with every project in the app.</li>
              <li><strong>Unlink</strong> — forgets the URL. Doesn&rsquo;t touch the sheet.</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button size="sm" onClick={() => onOpenChange(false)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
